/**
 * Sohbet rol yetkileri yönetim dialogu (yalnız sys-rol kullanıcıları görebilir).
 *
 * Her gönderen rol için hangi rollerin mesajlarını görebileceği / kimlerle
 * sohbet başlatabileceği burada yönetilir. Tablo boşken herkes herkesi görür;
 * bir rol için kural eklenince o rol yalnızca seçilen rolleri görür.
 *
 * Sunucu ucu: GET/POST /api/system/MessageRolePermissions  (#511)
 */
import { useTranslation } from 'react-i18next';
import { Info, ShieldCheck } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { useRoles } from '@/features/policy/api/policyApi';
import { useChatRolePermissionsManager } from '../api/chatRolePermissionsApi';

export function ChatRolePermissionsDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { data: roles = [] } = useRoles();
  const { matrix, toggle, save, loading, saving } = useChatRolePermissionsManager(roles);

  const hasRules = Object.values(matrix).some((s) => s.size > 0);

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="text-brand-600 h-4 w-4" />
          {t('chat.rolePermissionsTitle')}
        </span>
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common:Cancel')}
          </Button>
          <Button disabled={saving || loading} onClick={async () => { if (await save()) onClose(); }}>
            {t('common:Save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Bilgi bandı */}
        <div className="bg-brand-500/8 border-brand-300 flex items-start gap-2 rounded-md border px-3 py-2 text-sm">
          <Info className="text-brand-600 mt-0.5 h-4 w-4 shrink-0" />
          <span className="text-fg-muted">
            {hasRules ? t('chat.rolePermissionsDescActive') : t('chat.rolePermissionsDesc')}
          </span>
        </div>

        {/* Matris */}
        {loading ? (
          <p className="text-fg-subtle py-8 text-center text-sm">{t('common:Loading')}…</p>
        ) : roles.length === 0 ? (
          <p className="text-fg-subtle py-8 text-center text-sm">{t('chat.rolePermissionsNoRoles')}</p>
        ) : (
          <div className="flex flex-col divide-y divide-dashed">
            {roles.map((sender) => (
              <div key={sender.roleId} className="py-3">
                <p className="text-fg mb-2 text-sm font-semibold">{sender.roleName}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {roles.map((recipient) => (
                    <label
                      key={recipient.roleId}
                      className="flex cursor-pointer items-center gap-1.5 text-sm"
                    >
                      <Checkbox
                        checked={!!(matrix[sender.roleId]?.has(recipient.roleId))}
                        onCheckedChange={() => toggle(sender.roleId, recipient.roleId)}
                      />
                      <span className={recipient.roleId === sender.roleId ? 'text-fg-muted' : ''}>
                        {recipient.roleName}
                        {recipient.roleId === sender.roleId ? (
                          <span className="text-fg-subtle ml-1 text-[10px]">({t('chat.sameRole')})</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
